import type {
  ElementBasedTemplateConfig,
  TemplateElement,
  MastheadElement,
  IssueInfoElement,
  HeadlineElement,
  HeroImageElement,
  TextBlockElement,
  FooterBarElement,
  CoverLineElement,
  MagazineTitle,
  MagazineIssue,
} from "@shared/schema";

interface ElementBasedCoverRendererProps {
  config: ElementBasedTemplateConfig;
  title?: MagazineTitle | null;
  issue?: MagazineIssue | null;
  coverImage?: string | null;
  mainArticle?: {
    title: string;
    subtitle?: string | null;
    author?: string | null;
  } | null;
  secondaryArticles?: Array<{
    title: string;
    pageNumber?: number;
  }>;
  className?: string;
}

export function ElementBasedCoverRenderer({
  config,
  title,
  issue,
  coverImage,
  mainArticle,
  secondaryArticles = [],
  className = "",
}: ElementBasedCoverRendererProps) {
  const sortedElements = [...config.elements].sort((a, b) => a.zIndex - b.zIndex);

  const renderElement = (element: TemplateElement) => {
    if (!element.visible) return null;

    const baseStyle: React.CSSProperties = {
      position: "absolute",
      left: `${element.x}%`,
      top: `${element.y}%`,
      width: `${element.width}%`,
      height: `${element.height}%`,
      zIndex: element.zIndex,
      transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    };

    switch (element.type) {
      case "masthead":
        return renderMasthead(element as MastheadElement, baseStyle);
      case "issue_info":
        return renderIssueInfo(element as IssueInfoElement, baseStyle);
      case "main_headline":
        return renderMainHeadline(element as HeadlineElement, baseStyle);
      case "secondary_headline":
        return renderSecondaryHeadline(element as HeadlineElement, baseStyle);
      case "hero_image":
        return renderHeroImage(element as HeroImageElement, baseStyle);
      case "text_block":
        return renderTextBlock(element as TextBlockElement, baseStyle);
      case "footer_bar":
        return renderFooterBar(element as FooterBarElement, baseStyle);
      case "cover_line":
        return renderCoverLine(element as CoverLineElement, baseStyle);
      default:
        return null;
    }
  };

  const renderMasthead = (element: MastheadElement, baseStyle: React.CSSProperties) => {
    const logoUrl = element.logoUrl || title?.logo;
    
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          backgroundColor: element.fillProps?.backgroundColor || "transparent",
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
        }}
        className="flex items-center justify-center"
      >
        {logoUrl ? (
          <img 
            src={logoUrl} 
            alt={title?.name || "Magazine"} 
            className="max-h-full max-w-full object-contain"
          />
        ) : title?.name ? (
          <span 
            style={{ 
              fontFamily: element.textStyle?.fontFamily || "Inter",
              fontSize: `${element.textStyle?.fontSize || 24}px`,
              fontWeight: element.textStyle?.fontWeight || "bold",
              color: element.textStyle?.fontColor || "#000",
            }}
          >
            {title.name}
          </span>
        ) : null}
      </div>
    );
  };

  const renderIssueInfo = (element: IssueInfoElement, baseStyle: React.CSSProperties) => {
    const issueNumber = issue?.issueNumber;
    const issueDate = issue?.issueDate 
      ? new Date(issue.issueDate).toLocaleDateString("en-US", { month: "long", year: "numeric" })
      : null;
    
    const parts: string[] = [];
    if (element.showIssueNumber && issueNumber) {
      parts.push(`Issue ${issueNumber}`);
    }
    if (element.showDate && issueDate) {
      parts.push(issueDate);
    }
    
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          fontFamily: element.textStyle?.fontFamily || "Inter",
          fontSize: `${element.textStyle?.fontSize || 12}px`,
          color: element.textStyle?.fontColor || "#666",
          textAlign: element.textStyle?.textAlign || "right",
          backgroundColor: element.fillProps?.backgroundColor || "transparent",
        }}
        className="flex items-center justify-center"
      >
        <span className="truncate px-2">
          {parts.join(` ${element.separator || "•"} `) || "Issue Info"}
        </span>
      </div>
    );
  };

  const renderMainHeadline = (element: HeadlineElement, baseStyle: React.CSSProperties) => {
    const headlineText = mainArticle?.title || element.placeholderText || "Main Story Headline";
    const subtitleText = mainArticle?.subtitle || "Subtitle text";
    
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          fontFamily: element.textStyle?.fontFamily || "Inter",
          backgroundColor: element.fillProps?.backgroundColor || "transparent",
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
        }}
        className="flex flex-col justify-end p-2 overflow-hidden"
      >
        <div
          style={{
            fontSize: `${element.textStyle?.fontSize || 24}px`,
            fontWeight: element.textStyle?.fontWeight || "bold",
            color: element.textStyle?.fontColor || "#fff",
            lineHeight: element.textStyle?.lineHeight || 1.2,
            textAlign: element.textStyle?.textAlign || "left",
          }}
          className="line-clamp-3"
        >
          {headlineText}
        </div>
        {element.showSubtitle && (
          <div
            style={{
              fontSize: `${element.subtitleStyle?.fontSize || 14}px`,
              color: element.subtitleStyle?.fontColor || "#ccc",
            }}
            className="mt-1 line-clamp-2"
          >
            {subtitleText}
          </div>
        )}
      </div>
    );
  };

  const renderSecondaryHeadline = (element: HeadlineElement, baseStyle: React.CSSProperties) => {
    const elementIndex = config.elements
      .filter(e => e.type === "secondary_headline")
      .findIndex(e => e.id === element.id);
    
    const article = secondaryArticles[elementIndex];
    const headlineText = article?.title || element.placeholderText || "Secondary Headline";
    
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          fontFamily: element.textStyle?.fontFamily || "Inter",
          backgroundColor: element.fillProps?.backgroundColor || "transparent",
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
        }}
        className="flex flex-col justify-end p-2 overflow-hidden"
      >
        <div
          style={{
            fontSize: `${element.textStyle?.fontSize || 14}px`,
            fontWeight: element.textStyle?.fontWeight || "semibold",
            color: element.textStyle?.fontColor || "#fff",
            lineHeight: element.textStyle?.lineHeight || 1.2,
            textAlign: element.textStyle?.textAlign || "left",
          }}
          className="line-clamp-2"
        >
          {headlineText}
        </div>
      </div>
    );
  };

  const renderHeroImage = (element: HeroImageElement, baseStyle: React.CSSProperties) => {
    const imageUrl = coverImage || element.placeholderUrl;
    
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
          overflow: "hidden",
        }}
        className="relative"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Cover"
            className="w-full h-full"
            style={{
              objectFit: (element.fillProps?.backgroundSize as "cover" | "contain") || "cover",
              objectPosition: element.fillProps?.backgroundPosition || "center",
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-500" />
        )}
        {element.overlayEnabled && (
          <div
            className="absolute inset-0"
            style={{
              background: element.overlayGradient
                ? `linear-gradient(to ${element.overlayDirection || "bottom"}, transparent, ${element.overlayColor || "rgba(0,0,0,0.7)"})`
                : element.overlayColor || "rgba(0,0,0,0.5)",
              opacity: element.overlayOpacity || 0.5,
            }}
          />
        )}
      </div>
    );
  };

  const renderTextBlock = (element: TextBlockElement, baseStyle: React.CSSProperties) => {
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          fontFamily: element.textStyle?.fontFamily || "Inter",
          fontSize: `${element.textStyle?.fontSize || 14}px`,
          fontWeight: element.textStyle?.fontWeight || "normal",
          color: element.textStyle?.fontColor || "#000",
          textAlign: element.textStyle?.textAlign || "left",
          lineHeight: element.textStyle?.lineHeight || 1.4,
          backgroundColor: element.fillProps?.backgroundColor || "transparent",
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
        }}
        className="p-2 overflow-hidden"
      >
        {element.content || ""}
      </div>
    );
  };

  const renderFooterBar = (element: FooterBarElement, baseStyle: React.CSSProperties) => {
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          fontFamily: element.textStyle?.fontFamily || "Inter",
          fontSize: `${element.textStyle?.fontSize || 10}px`,
          color: element.textStyle?.fontColor || "#fff",
          backgroundColor: element.fillProps?.backgroundColor || "#333",
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
        }}
        className="flex items-center justify-center px-2"
      >
        <span className="truncate">
          {element.showAlsoInThisIssue 
            ? (element.alsoInThisIssueText || "Also in this issue...") 
            : "Footer content"}
        </span>
      </div>
    );
  };

  const renderCoverLine = (element: CoverLineElement, baseStyle: React.CSSProperties) => {
    return (
      <div
        key={element.id}
        style={{
          ...baseStyle,
          fontFamily: element.textStyle?.fontFamily || "Inter",
          fontSize: `${element.textStyle?.fontSize || 11}px`,
          fontWeight: element.textStyle?.fontWeight || "medium",
          color: element.textStyle?.fontColor || "#fff",
          textAlign: element.textStyle?.textAlign || "center",
          backgroundColor: element.fillProps?.backgroundColor || "transparent",
          borderRadius: element.fillProps?.borderRadius ? `${element.fillProps.borderRadius}px` : undefined,
        }}
        className="flex items-center justify-center px-2"
      >
        <span className="truncate">{element.content || ""}</span>
      </div>
    );
  };

  return (
    <div 
      className={`relative overflow-hidden bg-white ${className}`}
      style={{ aspectRatio: "3/4" }}
      data-testid="element-based-cover-renderer"
    >
      {sortedElements.map(renderElement)}
    </div>
  );
}
